// Export config options that are set on environment variables or configuration files
import firebaseConfigJSON from '../firebase.json';

export const appVersion = process.env.version || '0.0.0';

export const commitHash = process.env.commitHash || 'N/A';

export const nodeEnv = process.env.NODE_ENV;

export const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY || '',
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID || '',
  emulators: firebaseConfigJSON.emulators,
};

const rawReactAppUrl = process.env.NEXT_PUBLIC_REACT_APP_URL || '';
export const reactAppLoginRoute =
  process.env.NEXT_PUBLIC_REACT_APP_LOGIN_ROUTE || '';

/**
 * Returns `reactAppUrl`'s origin, or `undefined` if it's unset or malformed
 * (e.g. missing a protocol) rather than throwing — this feeds a same-origin
 * check, not a hard dependency, so a misconfigured value should degrade to
 * "not microfrontend" instead of crashing the whole app at import time.
 *
 * @returns {string | undefined}
 */
function getReactAppOrigin() {
  try {
    return new URL(rawReactAppUrl).origin;
  } catch {
    return undefined;
  }
}

// True when this app is served as a microfrontend, i.e. behind the legacy
// react-app's Firebase Hosting rewrites rather than on its own
// dispatch*.isheepdog.com domain. Both apps then share an origin, so the
// session cookie is shared and navigation between them stays same-origin.
//
// This can't be a build-time-only flag: the App Hosting backend is reachable
// both directly (standalone) and via the react-app rewrite (microfrontend),
// from the same build. So alongside the explicit override (set by
// `yarn start:proxy` in react-app for local development, ISD-4626), detect
// microfrontend mode at request time by comparing the current origin
// against reactAppUrl's origin instead of hardcoding hostname patterns.
export const isMicrofrontend =
  process.env.NEXT_PUBLIC_IS_MICROFRONTEND === 'true' ||
  (typeof window !== 'undefined' &&
    window.location.origin === getReactAppOrigin());

// Empty (same-origin/relative) when served as a microfrontend, since
// reactAppUrl's origin IS the current origin in that case — pointing at the
// full URL would leave the origin and defeat the point of the rewrites.
// Every route constant here already starts with a leading `/`, so this is
// always concatenated as `${reactAppUrl}${someRoute}`.
export const reactAppUrl = isMicrofrontend ? '' : rawReactAppUrl;

// Same as `reactAppUrl`, but for navigating to the legacy app's root with no
// route suffix — falls back to `/` instead of `''` so it's still a valid,
// same-origin destination on its own.
export const reactAppRootUrl = isMicrofrontend ? '/' : rawReactAppUrl;
export const reactAppJobsListRoute =
  process.env.NEXT_PUBLIC_REACT_APP_JOBS_LIST_ROUTE || '';
export const reactAppJobDetailsRoute =
  process.env.NEXT_PUBLIC_REACT_APP_JOB_DETAILS_ROUTE || '';
export const reactAppJobCreateRoute =
  process.env.NEXT_PUBLIC_REACT_APP_JOB_CREATE_ROUTE || '';
export const reactAppJobEditRoute =
  process.env.NEXT_PUBLIC_REACT_APP_JOB_EDIT_ROUTE || '';
export const reactAppOperatorDetailsRoute =
  process.env.NEXT_PUBLIC_REACT_APP_OPERATOR_DETAILS_ROUTE || '';
export const reactAppTaskDetailsRoute =
  process.env.NEXT_PUBLIC_REACT_APP_TASK_DETAILS_ROUTE || '';
export const reactAppInvoiceViewRoute =
  process.env.NEXT_PUBLIC_REACT_APP_INVOICE_VIEW_ROUTE || '';
export const reactAppReportViewRoute =
  process.env.NEXT_PUBLIC_REACT_APP_REPORT_VIEW_ROUTE || '';

// Name of the cloud function to check the current authentication status
export const authCheckCloudFunction =
  process.env.NEXT_PUBLIC_AUTH_CHECK_FUNCTION;

// Use this flag to determine if should connect firebase sdk to local emulators
export const useEmulators = process.env.NEXT_PUBLIC_USE_EMULATORS === 'true';

export const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
export const GEOCODE_URL = process.env.NEXT_PUBLIC_GEOCODE_URL;
// Map ID for the scheduler Map view, required by Advanced Markers. Set in .env to
// the vector "ISD Dispatch Scheduler Map" (project isheepdog-f8685, same project
// as the Maps API key); Google's DEMO_MAP_ID is only a fallback for testing.
export const GOOGLE_MAPS_MAP_ID =
  process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';

// Offer the simulated geotrack stream on the Live Map (local development only)
export const liveMapSimulatorEnabled =
  process.env.NEXT_PUBLIC_LIVE_MAP_SIMULATOR === 'true';

export const ISHEEPDOG_COMPANY_ID =
  process.env.NEXT_PUBLIC_ISHEEPDOG_COMPANY_ID || '';
export const ISD_DISPATCH_COMPANY_ID =
  process.env.NEXT_PUBLIC_ISD_DISPATCH_COMPANY_ID || '';

export const DEFAULT_AVATAR_URL =
  process.env.NEXT_PUBLIC_DEFAULT_AVATAR_URL || '';

export const algoliaConfig = {
  appId: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || '',
  indexNameOperatorTruck:
    process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME_OPERATOR_TRUCK || '',
  indexNameTickets: process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME_TICKETS || '',
};

export const SMARTLOOK_KEY = process.env.NEXT_PUBLIC_SMARTLOOK_KEY || '';
